
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { toast } from '@/utils/toast';
import webRTCService from '@/services/webrtc';

// Define message type
export interface ChatMessage {
  id: string;
  text: string;
  isOutgoing: boolean;
  timestamp: Date;
  isRead: boolean;
}

interface ChatContextType {
  connectToRoom: (roomId: string) => Promise<boolean>;
  leaveRoom: () => void;
  sendMessage: (message: string) => boolean;
  currentRoomId: string | null;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
  connectionStatus: 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';
  isSearchingForPartner: boolean;
  findNextPartner: () => Promise<void>;
  toggleMicrophone: (enabled: boolean) => void;
  toggleVideo: (enabled: boolean) => void;
  isMicrophoneEnabled: boolean;
  isVideoEnabled: boolean;
  onlineUsers: number;
  // Include activeRooms to fix error
  activeRooms: number;
  messages: ChatMessage[];
  isRemoteUserTyping: boolean;
  startTyping: () => void;
  stopTyping: () => void;
}

const defaultContext: ChatContextType = {
  connectToRoom: async () => false,
  leaveRoom: () => {},
  sendMessage: () => false,
  currentRoomId: null,
  remoteStream: null,
  localStream: null,
  connectionStatus: 'new',
  isSearchingForPartner: false,
  findNextPartner: async () => {},
  toggleMicrophone: () => {},
  toggleVideo: () => {},
  isMicrophoneEnabled: false,
  isVideoEnabled: false,
  onlineUsers: 0,
  activeRooms: 0,
  messages: [],
  isRemoteUserTyping: false,
  startTyping: () => {},
  stopTyping: () => {},
};

const ChatContext = createContext<ChatContextType>(defaultContext);

export const useChat = () => useContext(ChatContext);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentRoomId, setCurrentRoomId] = useState<string | null>(null);
  const [currentPeerId, setCurrentPeerId] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed'>('new');
  const [isSearchingForPartner, setIsSearchingForPartner] = useState(false);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isRemoteUserTyping, setIsRemoteUserTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<number | null>(null);
  
  // Messages state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  
  // Simulate random numbers for online users and active rooms (for demo purposes)
  const [onlineUsers, setOnlineUsers] = useState(Math.floor(Math.random() * 500) + 1800);
  const [activeRooms, setActiveRooms] = useState(Math.floor(Math.random() * 100) + 80);
  
  // Periodically update the numbers (for demo)
  useEffect(() => {
    const interval = setInterval(() => {
      const randomChangeUsers = Math.floor(Math.random() * 10) - 3; // Random change between -3 and +6
      const randomChangeRooms = Math.floor(Math.random() * 4) - 1; // Random change between -1 and +2
      
      setOnlineUsers(prev => Math.max(1500, prev + randomChangeUsers));
      setActiveRooms(prev => Math.max(70, prev + randomChangeRooms));
    }, 10000); // Update every 10 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  // Initialize WebRTC service when the component mounts
  useEffect(() => {
    const initWebRTC = async () => {
      try {
        if (!currentUser) {
          console.log("No user logged in, skipping WebRTC initialization");
          return;
        }
        
        const stream = await webRTCService.initialize();
        setLocalStream(stream);
        
        webRTCService.onRemoteStream((stream, peerId) => {
          console.log(`Received remote stream from peer ${peerId}`);
          setRemoteStream(stream);
        });
        
        webRTCService.onConnectionStateChange((state, peerId) => {
          console.log(`Connection state changed to ${state} for peer ${peerId}`);
          setConnectionStatus(state);
          
          if (state === 'connected') {
            setIsSearchingForPartner(false);
          }
        });
        
        // Setup message handler - manually handle messages since onMessage isn't in the type
        // @ts-ignore - We know this exists but it's not in the type definition
        webRTCService.onMessage && webRTCService.onMessage((data: any, peerId: string) => {
          if (data.type === 'chat-message') {
            const newMessage: ChatMessage = {
              id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
              text: data.content,
              isOutgoing: false,
              timestamp: new Date(),
              isRead: false
            };
            setMessages(prev => [...prev, newMessage]);
          } else if (data.type === 'typing') {
            setIsRemoteUserTyping(true);
            // Clear typing indicator after 3 seconds
            setTimeout(() => setIsRemoteUserTyping(false), 3000);
          } else if (data.type === 'typing-stopped') {
            setIsRemoteUserTyping(false);
          }
        });
      } catch (error) {
        console.error("Failed to initialize WebRTC:", error);
        toast.error("Failed to initialize camera and microphone");
      }
    };
    
    initWebRTC();
    
    // Cleanup on unmount
    return () => {
      webRTCService.dispose().catch(err => {
        console.error("Error disposing WebRTC service:", err);
      });
    };
  }, [currentUser]);
  
  // Connect to a room
  const connectToRoom = useCallback(async (roomId: string): Promise<boolean> => {
    try {
      if (!currentUser) {
        throw new Error("You must be logged in to connect to a room");
      }
      
      setCurrentRoomId(roomId);
      setConnectionStatus('connecting');
      
      // Mock connection with dummy video for demo
      const peerId = await webRTCService.mockConnection('/placeholder.svg');
      setCurrentPeerId(peerId);
      
      // Set searching state
      setIsSearchingForPartner(true);
      
      return true;
    } catch (error) {
      console.error("Error connecting to room:", error);
      setConnectionStatus('failed');
      throw error;
    }
  }, [currentUser]);
  
  // Leave the current room
  const leaveRoom = useCallback(() => {
    try {
      if (currentPeerId) {
        webRTCService.closePeerConnection(currentPeerId);
      }
      
      setCurrentRoomId(null);
      setCurrentPeerId(null);
      setRemoteStream(null);
      setConnectionStatus('closed');
      setIsSearchingForPartner(false);
    } catch (error) {
      console.error("Error leaving room:", error);
    }
  }, [currentPeerId]);
  
  // Find a new partner
  const findNextPartner = useCallback(async () => {
    try {
      if (!currentUser) {
        throw new Error("You must be logged in to find a partner");
      }
      
      if (currentPeerId) {
        await webRTCService.closePeerConnection(currentPeerId);
      }
      
      setRemoteStream(null);
      setConnectionStatus('connecting');
      setIsSearchingForPartner(true);
      
      // Mock connection with dummy video for demo
      const peerId = await webRTCService.mockConnection('/placeholder.svg');
      setCurrentPeerId(peerId);
      
    } catch (error) {
      console.error("Error finding next partner:", error);
      setConnectionStatus('failed');
      throw error;
    }
  }, [currentUser, currentPeerId]);
  
  // Send a message to the current peer
  const sendMessage = useCallback((message: string): boolean => {
    if (!currentPeerId) {
      return false;
    }
    
    // Create a new message object
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      text: message,
      isOutgoing: true,
      timestamp: new Date(),
      isRead: false
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    return webRTCService.sendMessage(currentPeerId, {
      type: 'chat-message',
      content: message
    });
  }, [currentPeerId]);
  
  // Toggle microphone
  const toggleMicrophone = useCallback((enabled: boolean) => {
    webRTCService.toggleAudioTrack(enabled);
    setIsMicrophoneEnabled(enabled);
  }, []);
  
  // Toggle video
  const toggleVideo = useCallback((enabled: boolean) => {
    webRTCService.toggleVideoTrack(enabled);
    setIsVideoEnabled(enabled);
  }, []);
  
  // Handle user typing indication
  const startTyping = useCallback(() => {
    if (!currentPeerId) return;
    
    // Send typing indicator to peer
    webRTCService.sendMessage(currentPeerId, { type: 'typing' });
    
    // Clear existing timeout if there is one
    if (typingTimeout) {
      window.clearTimeout(typingTimeout);
    }
    
    // Set new timeout
    const timeout = window.setTimeout(() => {
      stopTyping();
    }, 2000);
    
    setTypingTimeout(Number(timeout));
  }, [currentPeerId, typingTimeout]);
  
  const stopTyping = useCallback(() => {
    if (!currentPeerId) return;
    
    // Send typing stopped indicator
    webRTCService.sendMessage(currentPeerId, { type: 'typing-stopped' });
    
    // Clear timeout
    if (typingTimeout) {
      window.clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
  }, [currentPeerId, typingTimeout]);
  
  const value = {
    connectToRoom,
    leaveRoom,
    sendMessage,
    currentRoomId,
    remoteStream,
    localStream,
    connectionStatus,
    isSearchingForPartner,
    findNextPartner,
    toggleMicrophone,
    toggleVideo,
    isMicrophoneEnabled,
    isVideoEnabled,
    onlineUsers,
    activeRooms,
    messages,
    isRemoteUserTyping,
    startTyping,
    stopTyping
  };
  
  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
